import { Component, Input, OnDestroy, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ReliabilityFeedback } from 'src/app/shared/models/gql/carer-reliability.model';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { CancelEvaluationConfirmationModal } from '../../cancel-evaluation-confirmation-modal/cancel-evaluation-confirmation-modal';
import { ReliabilityAssessmentService } from '../../services/reliability-assessment.service';
import { WeeklyEvaluationStep2 } from '../step2/step2.page';

@Component({
  selector: 'app-step1',
  templateUrl: './step1.page.html',
  styleUrls: ['./step1.page.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class WeeklyEvaluationStep1 implements OnDestroy {
  public form: UntypedFormGroup;

  @Input() assessmentId: string;

  // subject for onDestroy, used to unsubscribe from subscriptions
  onDestroySubject = new Subject<void>();

  constructor(
    public modalCtrl: ModalController,
    public fb: UntypedFormBuilder,
    public assessmentService: ReliabilityAssessmentService,
    private utilityService: UtilityService,
  ) {
    this.setupFormFields();
    this.watchForValueChanges();
    this.getCarerReliabilityAssessmentData();
  }

  /**
   * Get Assessment data
   */
  async getCarerReliabilityAssessmentData() {
    const requestLoader = await this.utilityService.showLoading();
    try {
      const resData = await this.assessmentService.getReliabilityAssessmentData(this.assessmentId);
      if (resData) {
        this.assessmentService.assessmentData = resData;
      }
    } catch (error) {
      this.utilityService.showErrorAlert('Unable to perform action. Please try again later');
    } finally {
      this.utilityService.hideLoading(requestLoader);
    }
  }

  /**
   * setup form fields
   */
  setupFormFields() {
    this.form = this.fb.group({
      scheduledSessions: ['', [Validators.required, Validators.min(0)]],
      noShows: [null, [Validators.required, Validators.min(0), Validators.max(0)]],
    });
  }

  /**
   * Continue to steps2 functionality or submit the weekly evaluation form
   */
  continue() {
    if (this.form.valid) {
      // no any schedule session than direct submit the form.
      if (this.form.value.scheduledSessions === 0) {
        const requestObj: ReliabilityFeedback = {
          id: this.assessmentId,
          carerReliabilityAttributes: {
            scheduledSessions: this.form.value.scheduledSessions,
          },
        };
        this.submitEvaluation(requestObj);
      } else {
        this.navigateToStep2();
      }
    } else {
      this.form.markAllAsTouched();
    }
  }

  /**
   * Navigate to step2 page
   */
  async navigateToStep2() {
    const lateOrNoSessions = [];
    // calculate late or no-show session based on current input of scheduleSession and noShows.
    for (let index = 0; index <= this.form.value.scheduledSessions - this.form.value.noShows; index++) {
      lateOrNoSessions.push({ id: index, name: index });
    }
    const step2Modal = await this.modalCtrl.create({
      component: WeeklyEvaluationStep2,
      cssClass: 'fullpage-modal',
      componentProps: {
        lateOrNoSessions,
        assessmentValue: this.form.value,
      },
    });

    // https://github.com/ionic-team/ionic-framework/issues/26872
    step2Modal.shadowRoot.querySelector('.modal-wrapper').removeAttribute('aria-modal');

    step2Modal.onDidDismiss().then(async (res) => {
      if (res?.data) {
        const requestObj: ReliabilityFeedback = {
          id: this.assessmentId,
          carerReliabilityAttributes: {
            ...this.form.value,
            ...res.data,
          },
        };
        this.submitEvaluation(requestObj);
      }
    });
    await step2Modal.present();
  }

  /**
   * submit the evaluation details.
   *
   * @param requestObj - Evaluation data object
   */
  async submitEvaluation(requestObj: ReliabilityFeedback) {
    const requestLoader = await this.utilityService.showLoading();
    try {
      const resData = await this.assessmentService.postCarerReliabilityFeedback(requestObj);
      if (resData) {
        await this.utilityService.showAlert('Success', 'Your evaluation has been submitted');
        this.modalCtrl.dismiss();
        this.assessmentService.refresh();
      }
    } catch (error) {
      this.utilityService.showErrorAlert('Unable to perform action. Please try again later');
    } finally {
      this.utilityService.hideLoading(requestLoader);
    }
  }

  /**
   * Open the Confirmation modal click on Cancel CTA
   */
  async cancel() {
    // display confirmation modal on cancel CTA
    const confirmModal = await this.modalCtrl.create({
      component: CancelEvaluationConfirmationModal,
      cssClass: 'fullpage-modal',
      componentProps: {
        workerName: this.assessmentService.assessmentData?.agreement?.otherUser?.firstName,
      },
    });

    // https://github.com/ionic-team/ionic-framework/issues/26872
    confirmModal.shadowRoot.querySelector('.modal-wrapper').removeAttribute('aria-modal');

    confirmModal.onDidDismiss().then(async (res) => {
      if (res.data?.optout) {
        // when optout is true then close modal and refresh the dashboard page
        this.modalCtrl.dismiss(null, null, 'weekly-evaluation-modal');
        this.assessmentService.refresh();
      } else if (res.data?.doItLater) {
        this.modalCtrl.dismiss(null, null, 'weekly-evaluation-modal');
      }
    });
    await confirmModal.present();
  }

  /**
   * Show validation for schedule session and no-shows.
   * when no-shows value is grater than schedule session show the error message and wise versa.
   */
  watchForValueChanges() {
    this.form
      .get('scheduledSessions')
      .valueChanges.pipe(takeUntil(this.onDestroySubject))
      .subscribe((value) => {
        if (value === 0) {
          this.form.get('noShows').reset();
          this.form.get('noShows').setValidators([]);
          this.form.get('noShows').updateValueAndValidity({ emitEvent: false });
        } else {
          this.form
            .get('noShows')
            .setValidators([
              Validators.required,
              Validators.min(0),
              Validators.max(
                this.form.get('scheduledSessions').value <= 0 ? 0 : this.form.get('scheduledSessions').value,
              ),
            ]);
          this.form.get('noShows').updateValueAndValidity({ emitEvent: false });
        }
      });
    this.form
      .get('noShows')
      .valueChanges.pipe(takeUntil(this.onDestroySubject))
      .subscribe(() => {
        this.form
          .get('scheduledSessions')
          .setValidators([
            Validators.required,
            Validators.min(this.form.get('scheduledSessions').value <= 0 ? 0 : this.form.get('noShows').value),
          ]);
        this.form.get('scheduledSessions').updateValueAndValidity({ emitEvent: false });
      });
  }

  ngOnDestroy() {
    // we've destroyed the component, so update the subject and clear assessmentData
    this.assessmentService.assessmentData = null;
    this.onDestroySubject.next();
    this.onDestroySubject.complete();
  }
}
